import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, Download, Heart, Loader2, Newspaper } from "lucide-react";
import { toast } from "sonner";
import { API, api, formatApiError, getFingerprint } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { BlogCard } from "./BlogsPage";

export default function BlogPostPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(0);
  const [liking, setLiking] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api
      .get(`/blogs/${slug}`, { params: { fp: getFingerprint() } })
      .then((r) => {
        setData(r.data);
        setLiked(r.data.blog.liked || false);
        setLikes(r.data.blog.likes || 0);
      })
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
        else toast.error(formatApiError(e));
      });
  }, [slug]);

  const toggleLike = async () => {
    if (liking || !data) return;
    setLiking(true);
    try {
      const { data: res } = await api.post(`/blogs/${data.blog.id}/like`, { fingerprint: getFingerprint() });
      setLiked(res.liked);
      setLikes(res.likes);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLiking(false);
    }
  };

  if (notFound) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bone px-4 pt-[72px]" data-testid="blog-not-found">
        <div className="text-center">
          <Newspaper className="mx-auto h-10 w-10 text-forest/25" />
          <h1 className="mt-4 font-display text-2xl font-extrabold text-forest-deep">Article not found</h1>
          <p className="mt-2 text-sm text-muted-foreground">It may have been unpublished or moved.</p>
          <Link to="/blogs" data-testid="back-to-blogs"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-forest px-7 py-3 font-display text-sm font-bold text-bone">
            <ArrowLeft className="h-4 w-4" /> Back to articles
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bone pt-[72px]">
        <Loader2 className="h-8 w-8 animate-spin text-forest" />
      </div>
    );
  }

  const { blog, related } = data;

  return (
    <div className="min-h-screen bg-bone pt-[72px]" data-testid="blog-post-page">
      <article className="mx-auto max-w-3xl px-4 py-14 sm:px-6">
        <Reveal>
          <Link to="/blogs" data-testid="back-to-blogs"
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground transition-colors hover:text-forest">
            <ArrowLeft className="h-3.5 w-3.5" /> All articles
          </Link>
        </Reveal>

        <Reveal delay={0.06}>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <span className="rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-gold-dark">
              {blog.category}
            </span>
            {(blog.tags || []).map((t) => (
              <span key={t} className="rounded-full bg-secondary px-3 py-1.5 text-[11px] font-semibold text-forest/70">
                #{t}
              </span>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <h1 className="mt-6 font-display text-3xl font-extrabold leading-tight tracking-tight text-forest-deep sm:text-4xl lg:text-[2.75rem]">
            {blog.title}
          </h1>
        </Reveal>

        <Reveal delay={0.18}>
          <div className="mt-5 flex flex-wrap items-center gap-4 border-b border-border pb-6 text-sm text-muted-foreground">
            <span className="font-semibold text-forest">{blog.author}</span>
            <span>{blog.published_at && new Date(blog.published_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
            <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-gold-dark" />{blog.read_time || 1} min read</span>
          </div>
        </Reveal>

        {blog.image_url && (
          <Reveal delay={0.24}>
            <div className="mt-8 overflow-hidden rounded-3xl border border-border">
              <img src={blog.image_url} alt={blog.title} className="aspect-[16/8] w-full object-cover" />
            </div>
          </Reveal>
        )}

        <Reveal delay={0.3}>
          <div className="prose-neutral mt-10" data-testid="blog-content">
            {(blog.content || "").split("\n\n").map((para, i) => (
              <p key={i} className={`mb-6 leading-[1.85] text-foreground/85 ${i === 0 ? "font-editorial text-xl italic text-forest-deep" : "text-base"}`}>
                {para}
              </p>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.36}>
          <div className="mt-10 flex flex-wrap items-center gap-4 rounded-3xl border border-border bg-white p-6">
            <button onClick={toggleLike} data-testid="blog-like-btn" disabled={liking}
              className={`flex items-center gap-2.5 rounded-full border px-6 py-3 font-display text-sm font-bold transition-all duration-200 active:scale-95 ${
                liked
                  ? "border-gold bg-gold/15 text-gold-dark"
                  : "border-border bg-white text-foreground/60 hover:border-gold/50 hover:text-gold-dark"
              }`}>
              <Heart className={`h-5 w-5 transition-transform duration-200 ${liked ? "fill-gold text-gold scale-110" : ""}`} />
              {liked ? "Liked" : "Like this article"} · {likes}
            </button>
            <a href={`${API}/blogs/${blog.id}/download`} data-testid="blog-download-btn" download
              className="flex items-center gap-2.5 rounded-full bg-forest px-6 py-3 font-display text-sm font-bold text-bone transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(19,78,58,0.35)]">
              <Download className="h-4 w-4" /> Download PDF
            </a>
          </div>
        </Reveal>
      </article>

      {related.length > 0 && (
        <section className="border-t border-border bg-white py-16" data-testid="related-posts">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 className="font-display text-2xl font-bold text-forest-deep">
                Keep <em className="font-editorial font-medium italic text-gold-dark">reading</em>
              </h2>
            </Reveal>
            <div className="mt-8 grid gap-6 md:grid-cols-3">
              {related.map((b, i) => (
                <BlogCard key={b.id} blog={b} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
